import React, { Component, createRef } from 'react';

// CSS
import '../Courses/Courses.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, uploadCourseMedia } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';

// Properties
const properties = require('../../properties.json');

const EMPTY_FORM = { id: null, title: '', tag: '', videoUrl: '', photos: [] };

/** Manages the portfolio "Project" cards shown on the public Gallery page -- see
 *  GalleryProjectController on the backend. Each project is a title + short tag + several photos
 *  + one optional behind-the-scenes video, matching the reference design the teacher sent. */
class GalleryProjects extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        projects: [],
        loading: true,
        form: EMPTY_FORM,
        saving: false,
        uploadingPhotos: {}, // fileName -> percent, while a batch of photos is uploading
        uploadingVideo: false,
        videoProgress: 0,
    }

    componentDidMount() {
        document.title = properties.site_name + " | معرض الأعمال";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        this.load();
    }

    load = () => {
        apiFetch("/gallery-projects/mine")
            .then(projects => this.setState({ projects, loading: false }))
            .catch(error => {
                console.error('Error:', error);
                this.setState({ loading: false });
            });
    }

    resetForm = () => this.setState({ form: EMPTY_FORM });

    editProject = (project) => {
        this.setState({ form: { id: project.id, title: project.title || '', tag: project.tag || '', videoUrl: project.videoUrl || '', photos: project.photos || [] } });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    deleteProject = (project) => {
        if (!window.confirm(`هل أنت متأكد من حذف مشروع "${project.title}"؟`)) return;
        apiFetch(`/gallery-projects/${project.id}`, { method: "DELETE" })
            .then(() => this.load())
            .catch(error => {
                console.error('Error:', error);
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    uploadPhotos = async (event) => {
        const files = event.files || [];
        if (!files.length) return;
        const newUrls = [];
        for (const file of files) {
            this.setState(s => ({ uploadingPhotos: { ...s.uploadingPhotos, [file.name]: 0 } }));
            try {
                const url = await uploadCourseMedia(file, (pct) =>
                    this.setState(s => ({ uploadingPhotos: { ...s.uploadingPhotos, [file.name]: pct } }))
                );
                newUrls.push(url);
            } catch (error) {
                console.error('Error:', error);
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: `فشل رفع ${file.name}` });
            } finally {
                this.setState(s => {
                    const remaining = { ...s.uploadingPhotos };
                    delete remaining[file.name];
                    return { uploadingPhotos: remaining };
                });
            }
        }
        this.setState(s => ({ form: { ...s.form, photos: [...s.form.photos, ...newUrls] } }));
        event.options && event.options.clear && event.options.clear();
    }

    removePhoto = (index) => {
        this.setState(s => ({ form: { ...s.form, photos: s.form.photos.filter((_, i) => i !== index) } }));
    }

    // Cover has no dedicated field -- GalleryProjectPhoto's sortOrder doubles as it (see backend
    // GalleryProjectService), so index 0 in this array IS the cover. Moving the chosen photo there
    // is the whole implementation, and it keeps working identically on edit (photos load back into
    // this same array from the saved order).
    setCover = (index) => {
        this.setState(s => {
            const photos = [...s.form.photos];
            const [chosen] = photos.splice(index, 1);
            photos.unshift(chosen);
            return { form: { ...s.form, photos } };
        });
    }

    uploadVideo = async (event) => {
        const file = event.files && event.files[0];
        if (!file) return;
        this.setState({ uploadingVideo: true, videoProgress: 0 });
        try {
            const url = await uploadCourseMedia(file, (pct) => this.setState({ videoProgress: pct }));
            this.setState(s => ({ form: { ...s.form, videoUrl: url }, uploadingVideo: false }));
        } catch (error) {
            console.error('Error:', error);
            this.setState({ uploadingVideo: false });
            this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'فشل رفع الفيديو' });
        } finally {
            event.options && event.options.clear && event.options.clear();
        }
    }

    save = () => {
        const { form, projects } = this.state;
        if (!form.title || form.photos.length === 0) {
            this.toast.current.show({ severity: 'error', summary: 'تنبيه', detail: 'من فضلك أدخل عنوان المشروع وارفع صورة واحدة على الأقل' });
            return;
        }
        this.setState({ saving: true });
        const body = {
            title: form.title,
            tag: form.tag,
            videoUrl: form.videoUrl || null,
            photos: form.photos,
            sortOrder: form.id ? undefined : projects.length,
        };
        const request = form.id
            ? apiFetch(`/gallery-projects/${form.id}`, { method: "PUT", body })
            : apiFetch("/gallery-projects", { method: "POST", body });
        request
            .then(() => {
                this.setState({ saving: false, form: EMPTY_FORM });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم حفظ المشروع' });
                this.load();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    render() {
        const { projects, loading, form, saving, uploadingPhotos, uploadingVideo, videoProgress } = this.state;

        return (
            <div className="add-lesson-page">
                <Header pageName="معرض الأعمال" />
                <Toast ref={this.toast}></Toast>

                <div className="content">
                    <div className="container">
                        <Card title={form.id ? "تعديل مشروع" : "إضافة مشروع جديد"}>
                            <div className="p-grid p-fluid">
                                <div className="p-col-12 p-md-6">
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText value={form.title} onChange={(e) => this.setState({ form: { ...form, title: e.target.value } })} />
                                            <label>عنوان المشروع</label>
                                        </span>
                                    </div>
                                </div>
                                <div className="p-col-12 p-md-6">
                                    <div className="p-inputgroup">
                                        <span className="p-float-label">
                                            <InputText value={form.tag} onChange={(e) => this.setState({ form: { ...form, tag: e.target.value } })} placeholder="مثال: تصميم حضري · من الاسكتش للرندر" />
                                            <label>تصنيف/وصف مختصر</label>
                                        </span>
                                    </div>
                                </div>

                                <div className="p-col-12 p-md-12" style={{ marginTop: 10 }}>
                                    <FileUpload
                                        name="galleryPhotos"
                                        customUpload
                                        uploadHandler={this.uploadPhotos}
                                        auto
                                        multiple
                                        accept="image/*"
                                        maxFileSize={20000000}
                                        chooseLabel="رفع صور المشروع"
                                        uploadLabel="رفع"
                                        cancelLabel="إلغاء"
                                        mode="basic"
                                    />
                                    {Object.entries(uploadingPhotos).map(([name, pct]) => (
                                        <div className="upload-progress-row" key={name}>
                                            <span>{name}</span>
                                            <ProgressBar value={pct} style={{ flex: 1, height: '10px' }} />
                                            <span>{pct}%</span>
                                        </div>
                                    ))}
                                    {form.photos.length > 0 && (
                                        <div className="gallery-photo-list">
                                            {form.photos.map((url, i) => (
                                                <div className={`gallery-photo-row ${i === 0 ? 'is-cover' : ''}`} key={url + i}>
                                                    <img src={url} alt={`صورة ${i + 1}`} />
                                                    {i === 0 ? (
                                                        <span className="gallery-photo-cover-badge"><i className="pi pi-star-fill" /> صورة الغلاف</span>
                                                    ) : (
                                                        <Button label="تعيين كغلاف" className="p-button-text p-button-sm gallery-photo-set-cover" onClick={() => this.setCover(i)} />
                                                    )}
                                                    <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.removePhoto(i)} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="p-col-12 p-md-12">
                                    <FileUpload
                                        name="galleryVideo"
                                        customUpload
                                        uploadHandler={this.uploadVideo}
                                        auto
                                        disabled={uploadingVideo}
                                        multiple={false}
                                        accept="video/*"
                                        maxFileSize={2000000000}
                                        chooseLabel={form.videoUrl ? "استبدال فيديو المشروع" : "رفع فيديو المشروع (اختياري)"}
                                        uploadLabel="رفع"
                                        cancelLabel="إلغاء"
                                        mode="basic"
                                    />
                                    {uploadingVideo && (
                                        <div className="upload-progress-row">
                                            <ProgressBar value={videoProgress} style={{ flex: 1, height: '10px' }} />
                                            <span>{videoProgress}%</span>
                                        </div>
                                    )}
                                    {!uploadingVideo && form.videoUrl && (
                                        <div className="uploaded-file-row">
                                            <i className="pi pi-check-circle" style={{ color: 'green' }} />
                                            <span>تم رفع الفيديو</span>
                                            <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger" onClick={() => this.setState({ form: { ...form, videoUrl: '' } })} />
                                        </div>
                                    )}
                                </div>

                                <div className="p-col-12 p-md-12" style={{ display: 'flex', gap: 10 }}>
                                    <Button className={`add-button ${saving ? "loading" : ""}`} label={saving ? "جاري الحفظ..." : (form.id ? "حفظ التعديلات" : "إضافة المشروع")} onClick={this.save} />
                                    {form.id && <Button label="إلغاء" className="p-button-text" onClick={this.resetForm} />}
                                </div>
                            </div>
                        </Card>

                        <Card title="كل المشاريع" style={{ marginTop: 20 }}>
                            {loading ? <p>جاري التحميل...</p> : projects.length === 0 ? (
                                <p>لسه مفيش مشاريع مضافة.</p>
                            ) : (
                                projects.map((project) => (
                                    <div className="course-item-row" key={project.id}>
                                        <div className="course-item-info">
                                            {project.photos && project.photos[0] && <img src={project.photos[0]} alt={project.title} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }} />}
                                            <span>{project.title}</span>
                                            <span style={{ color: '#888', fontSize: 12 }}>{project.tag}</span>
                                            <span style={{ color: '#888', fontSize: 12 }}>{(project.photos || []).length} صورة{project.videoUrl ? ' + فيديو' : ''}</span>
                                        </div>
                                        <div>
                                            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" onClick={() => this.editProject(project)} />
                                            <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.deleteProject(project)} />
                                        </div>
                                    </div>
                                ))
                            )}
                        </Card>
                    </div>
                </div>
            </div>
        );
    }
}

export default GalleryProjects;
